import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas"]

  connect() {
    this.squares = []
    this.cols = 0
    this.rows = 0
    this.gap = 4
    this.size = 8
    this.step = this.size + this.gap
    this.timeouts = new Set()

    this.updateColors()
    this.buildGrid()
    this.startFlicker()

    this.resizeHandler = this.buildGrid.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    this.themeHandler = () => { this.updateColors(); this.recolorAll() }
    window.addEventListener("themeChanged", this.themeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    window.removeEventListener("themeChanged", this.themeHandler)
    if (this.flickerInterval) clearInterval(this.flickerInterval)
    this.timeouts.forEach(t => clearTimeout(t))
  }

  get isDark() {
    return document.documentElement.classList.contains("dark")
  }

  updateColors() {
    if (this.isDark) {
      this.baseColor = "rgba(255, 255, 255, 0.04)"
      this.accentColor = "rgba(229, 176, 112, 0.4)"
      this.brightColor = "rgba(255, 255, 255, 0.25)"
      this.mediumColor = "rgba(255, 255, 255, 0.14)"
    } else {
      this.baseColor = "rgba(0, 0, 0, 0.04)"
      this.accentColor = "rgba(229, 176, 112, 0.35)"
      this.brightColor = "rgba(0, 0, 0, 0.12)"
      this.mediumColor = "rgba(0, 0, 0, 0.07)"
    }
  }

  recolorAll() {
    // Clear all pending fade-back timeouts so old colors don't bleed through
    this.timeouts.forEach(t => clearTimeout(t))
    this.timeouts.clear()

    // Instant recolor — kill transition, set base color, restore transition
    this.squares.forEach(sq => {
      sq.style.transition = "none"
      sq.style.backgroundColor = this.baseColor
    })
    // Force reflow then restore transitions
    void this.element.offsetHeight
    requestAnimationFrame(() => {
      this.squares.forEach(sq => {
        sq.style.transition = "background-color 0.6s ease-in"
      })
    })
  }

  buildGrid() {
    const canvas = this.canvasTarget
    const rect = canvas.parentElement.getBoundingClientRect()
    const w = rect.width
    const h = rect.height

    this.cols = Math.ceil(w / this.step)
    this.rows = Math.ceil(h / this.step)
    const total = this.cols * this.rows

    canvas.innerHTML = ""
    this.squares = []

    canvas.style.width = `${this.cols * this.step}px`
    canvas.style.height = `${this.rows * this.step}px`
    canvas.style.display = "grid"
    canvas.style.gridTemplateColumns = `repeat(${this.cols}, ${this.size}px)`
    canvas.style.gap = `${this.gap}px`

    for (let i = 0; i < total; i++) {
      const sq = document.createElement("div")
      sq.style.width = `${this.size}px`
      sq.style.height = `${this.size}px`
      sq.style.borderRadius = "2px"
      sq.style.backgroundColor = this.baseColor
      sq.style.transition = "background-color 0.6s ease-in"
      canvas.appendChild(sq)
      this.squares.push(sq)
    }
  }

  startFlicker() {
    this.flickerBatch()
    this.flickerInterval = setInterval(() => {
      this.flickerBatch()
    }, 400)
  }

  flickerBatch() {
    const total = this.squares.length
    if (total === 0) return

    const count = Math.max(2, Math.floor(total * 0.025))

    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * total)
      const sq = this.squares[idx]
      const rand = Math.random()

      // Flash ON quickly
      sq.style.transition = "background-color 0.15s ease-out"

      if (rand < 0.25) {
        sq.style.backgroundColor = this.accentColor
      } else if (rand < 0.55) {
        sq.style.backgroundColor = this.brightColor
      } else {
        sq.style.backgroundColor = this.mediumColor
      }

      // Fade back slowly after random delay
      const fadeDelay = 600 + Math.random() * 1200
      const t = setTimeout(() => {
        sq.style.transition = "background-color 1.8s ease-in"
        sq.style.backgroundColor = this.baseColor
        this.timeouts.delete(t)
      }, fadeDelay)
      this.timeouts.add(t)
    }
  }
}
